import React from 'react';

/**
 * Generic detail view for any credential-based integration described in
 * utils/integrationsRegistry.js (CRED_CONFIG) — a form + setup-guide two
 * column layout. Used for WhatsApp/Gupshup, Email, Google Ads, AI Calling,
 * Gmail, Outlook, and Twilio so each one doesn't need its own bespoke JSX.
 *
 * Supports `type: 'select'` fields (used for provider pickers like AI
 * Calling and WhatsApp/BSP) in addition to plain text/password inputs.
 *
 * `extra` lets a specific integration slot in extra UI below the form
 * without forking this component — e.g. Email's "Verify DNS Records" check.
 *
 * `personalMode` (only meaningful when cfg.supportsPersonalMode is true) lets
 * the admin pick "Work account" (this company's own developer app — the
 * Client ID/Secret fields below) vs "Personal account" (employees connect
 * ANY personal account through one shared, platform-level app — no fields
 * needed here at all). Shape: { value: 'work'|'personal', onChange(mode),
 * available: boolean, loading: boolean }. See backend/src/routes/oauth.js
 * for how the mode is actually resolved/enforced server-side.
 */
export default function CredentialIntegrationDetail({
  cfg, credForm, setCredForm, credSaving, credSaved,
  isConnected, onSave, onDisconnect, onCancel, extra, personalMode,
}) {
  if (!cfg) return null;

  const showModeToggle = cfg.supportsPersonalMode && personalMode;
  const isPersonal = showModeToggle && personalMode.value === 'personal';
  // In "Personal account" mode, the company's own Client ID/Secret fields are
  // irrelevant — employees authorize through the shared platform app instead.
  const visibleFields = isPersonal ? [] : cfg.fields;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
      {/* Left: credential form */}
      <div>
        <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: cfg.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{cfg.icon}</div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{cfg.title}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{cfg.provider}</div>
            </div>
            {isConnected && (
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(16,185,129,0.2)' }}>
                ● Connected
              </span>
            )}
          </div>
          <p style={{ fontSize: 13, color: '#475569', marginBottom: cfg.note ? 14 : 20, lineHeight: 1.6 }}>{cfg.description}</p>

          {cfg.note && (
            <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#4338ca', lineHeight: 1.6 }}>
              💡 {cfg.note}
            </div>
          )}

          {showModeToggle && (
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>Account mode</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { value: 'work', label: 'Work account', help: "This company's own developer app — enter Client ID/Secret below" },
                  { value: 'personal', label: 'Personal account', help: 'Employees connect any personal account — no setup needed here' },
                ].map(opt => {
                  const disabled = opt.value === 'personal' && !personalMode.available;
                  const active = personalMode.value === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={disabled || personalMode.loading}
                      onClick={() => personalMode.onChange(opt.value)}
                      title={disabled ? "Not available yet — the platform operator hasn't enabled personal-account sign-in for this provider" : opt.help}
                      style={{
                        flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
                        border: `1.5px solid ${active ? cfg.color : 'rgba(0,0,0,0.1)'}`,
                        background: active ? cfg.color + '0e' : '#fff',
                        opacity: disabled ? 0.5 : 1,
                      }}
                    >
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: active ? cfg.color : '#0f172a' }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.4 }}>{opt.help}</div>
                    </button>
                  );
                })}
              </div>
              {isPersonal && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                  💡 No Client ID/Secret needed — employees will sign in with any {cfg.provider} account through a shared, platform-managed app.
                </div>
              )}
              {personalMode.value === 'personal' && !personalMode.available && (
                <div style={{ fontSize: 11, color: '#b45309', marginTop: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: '6px 10px' }}>
                  ⚠️ This was previously set to "Personal account" but it isn't available on this platform yet — connections will fail until "Work account" is configured or the operator enables personal mode.
                </div>
              )}
              {!isPersonal && !personalMode.loading && !personalMode.available && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 8, padding: '6px 10px', lineHeight: 1.5 }}>
                  🔒 "Personal account" is disabled because no shared {cfg.provider} app has been registered on this platform yet — that's a one-time setup the platform operator does (registering an app with {cfg.provider} and adding its credentials to the backend), separate from anything in this CRM's settings. Until then, use "Work account" with your own Client ID/Secret below.
                </div>
              )}
            </div>
          )}

          {visibleFields.map(field => (
            <div key={field.key} style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>{field.label}</label>

              {field.type === 'select' ? (
                <select
                  className="crm-input"
                  value={credForm[field.key] || ''}
                  onChange={e => setCredForm(p => ({ ...p, [field.key]: e.target.value }))}
                >
                  <option value="" disabled>Select…</option>
                  {(field.options || []).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  className="crm-input"
                  placeholder={field.placeholder}
                  value={credForm[field.key] || ''}
                  onChange={e => setCredForm(p => ({ ...p, [field.key]: e.target.value }))}
                  style={{ fontFamily: field.type === 'password' ? 'monospace' : 'inherit' }}
                />
              )}
              {field.help && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>💡 {field.help}</div>}
            </div>
          ))}

          <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center' }}>
            <button
              className="btn-primary"
              disabled={credSaving}
              onClick={onSave}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {credSaving ? '⏳ Saving…' : credSaved ? '✅ Saved!' : '💾 Save Credentials'}
            </button>
            {isConnected && (
              <button className="btn-ghost" onClick={onDisconnect} style={{ color: '#ef4444', fontSize: 12 }}>
                Disconnect
              </button>
            )}
            <button className="btn-ghost" onClick={onCancel} style={{ fontSize: 12, marginLeft: 'auto' }}>
              Cancel
            </button>
          </div>
        </div>

        {extra}
      </div>

      {/* Right: setup guide */}
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>🔧 Setup Guide</div>
        {cfg.setupSteps.map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: cfg.color + '15', color: cfg.color, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.55 }}>{step}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
